import * as destinationDeliveryService from "../../../services/destinationDelivery";
import * as settings from "../../../services/settings";

import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  notification,
} from "antd";
import { useCallback, useEffect, useState } from "react";

import { ModalDelete } from "../../../Components/model/modal/delete";
import dayjs from "dayjs";
import { formatDeliveryStatusLabel } from "../../../utils/deliveryStatusLabel";
import { isEmpty } from "../../../utils/common";
import permissions from "../../../../configs/permission";
import { useAccessToken } from "../../../contexts/AccessTokenContext";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../../hooks/usePermission";
import { useTranslation } from "react-i18next";

const { Link } = Typography;
const initPage = { limit: 10, page: 1 };

/** Coalesces concurrent fetches with identical params (e.g. React Strict Mode double mount). */
const pendingDestinationDeliveryByKey = new Map();

export const DestinationDeliveryPage = () => {
  const { hasPermission, isSuperuser } = usePermission();
  const { accessToken } = useAccessToken();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation("destinationdelivery", "buttons", "common");
  const { t: tOps } = useTranslation("options", { keyPrefix: "Package" });
  const [search, setSearch] = useState(initPage);
  const [destinationDeliveryData, setDestinationDeliveryData] = useState([]);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [prepareDeleteData, setPrepareDeleteData] = useState({});
  const [isOpenButton, setIsOpenButton] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowsValues, setSelectedRowsValues] = useState([]);
  const [countSelect, setCountSelect] = useState(0);
  const [tempSearch, setTempSearch] = useState();
  const [dateRange, setDateRange] = useState([]);
  const { RangePicker } = DatePicker;
  // Set the initial values for the form
  const initialValues = {
    depart_at: [dayjs().startOf("day"), dayjs().endOf("day")],
    search: undefined,
    delivery_status: "all",
    delivery_type: "all",
  };

  const selectDeliveryType = [
    { value: "all", label: tOps("All") },
    { value: "NORMAL", label: tOps("Delivery_Type.Normal") },
    { value: "SELF", label: tOps("Delivery_Type.Self") },
    { value: "PRIVATE", label: tOps("Delivery_Type.Private") },
    { value: "EXPRESS", label: tOps("Delivery_Type.Express") },
  ];

  const selectDeliveryStatus = [
    { value: "all", label: tOps("All") },
    { value: "open", label: formatDeliveryStatusLabel("open", i18n.language, tOps) },
    { value: "close", label: formatDeliveryStatusLabel("close", i18n.language, tOps) },
    { value: "deliver", label: formatDeliveryStatusLabel("deliver", i18n.language, tOps) },
    { value: "return", label: formatDeliveryStatusLabel("return", i18n.language, tOps) },
  ];

  const navigate = useNavigate();
  const { Title, Text } = Typography;
  const { Search } = Input;
  const [total, setTotal] = useState(0);

  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
    });
  };

  const onDeleteDestinationDelivery = async (data) => {
    try {
      if (Array.isArray(data) && data.length > 0) {
        const payload = { ids: data };
        await destinationDeliveryService.deleteMultiDestinationDeliveryPackage(accessToken, payload);
        setSelectedRowKeys([]);
        setSelectedRowsValues([]);
      } else {
        const prepareDeleteDestinationDelivery = { id: data.id };
        await destinationDeliveryService.deleteDestinationDelivery(accessToken, prepareDeleteDestinationDelivery);
      }
      openNotificationWithIcon(
        settings.NotificationType.SUCCESS,
        <>{"Success."}</>,
        <>{"Delete destination delivery success."}</>
      );
      setPrepareDeleteData({});
      setIsOpenDelete(false);
      fetchDestinationDelivery({ ...search });
      setLoading(false);
    } catch (err) {
      console.error("[DestinationDeliveryPage] onDeleteDestinationDelivery: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      setLoading(false);
    }
  };

  const fetchDestinationDelivery = useCallback(
    async (param) => {
      const requestKey = JSON.stringify(param);
      const existing = pendingDestinationDeliveryByKey.get(requestKey);
      if (existing) {
        return existing;
      }

      const promise = (async () => {
        try {
          const data = await destinationDeliveryService.getDestinationDelivery(accessToken, param);
          if (data) {
            setTotal(data.totalCount);
            setDestinationDeliveryData(data.queryDestinationDelivery);
          }
          setSearch({ ...param });
        } catch (err) {
          console.error("[DestinationDeliveryPage] fetchDestinationDelivery: ", err);
          const { message } = settings.getErrorMessage(err);
          openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
        } finally {
          pendingDestinationDeliveryByKey.delete(requestKey);
        }
      })();

      pendingDestinationDeliveryByKey.set(requestKey, promise);
      return promise;
    },
    [accessToken]
  );

  const onFinish = async () => {
    const values = form.getFieldsValue();
    setLoading(true);
    try {
      setTempSearch(values);
      await onSearch(values);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    form.resetFields();
    onSearch({ page: 1, limit: search.limit });
  };

  const onSearch = async (values) => {
    const { depart_at, search, delivery_status, delivery_type } = values;
    setLoading(true);
    try {
      let start_at;
      let end_at;

      if (depart_at) {
        start_at = dateRange[0] || undefined;
        end_at = dateRange[1] || undefined;
      }

      const searchText = search != undefined ? search.trim() : "";
      const params = {
        depart_at_from: start_at,
        depart_at_to: end_at,
        id: searchText,
        vehicle: searchText,
        delivery_status: normalizeSelection(delivery_status),
        delivery_type: normalizeSelection(delivery_type),
      };

      await fetchDestinationDelivery({
        ...params,
        page: values.page ?? initPage.page,
        limit: values.limit ?? initPage.limit,
      }); // Assume this function fetches the data
    } finally {
      setLoading(false);
    }
  };

  const normalizeSelection = (values) => {
    const modify = values !== "all" ? values : undefined;
    return modify;
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, value) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRowsValues(value);
      setCountSelect(value.length);
      setIsOpenButton(true);
    },
  };

  const handleExport = async () => {
    setLoading(true);
    const params = { page: 1, limit: total };
    try {
      const request = {
        ids: JSON.stringify(selectedRowKeys),
        ...params,
      };

      const response = await destinationDeliveryService.exportExcelZip(accessToken, request);

      if (response.status != 200) {
        throw new Error("Failed to export Excel file");
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "destination_packages.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[DestinationDeliveryPage] handleExport: ", err);
      let description = t("export_failed_desc", {
        ns: "common",
        defaultValue: "Failed to export file. Please try again.",
      });
      try {
        const blob = err?.response?.data;
        if (blob instanceof Blob) {
          const text = await blob.text();
          const json = JSON.parse(text);
          if (json?.message) description = json.message;
        }
      } catch (_) {
        // ใช้ fallback description
      }
      notification.error({
        message: t("export_failed", { ns: "common", defaultValue: "Export Failed" }),
        description,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await onSearch({ ...initPage });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const columns = [
    {
      title: t("Destination_Delivery_Table.Table_Vehicle"),
      dataIndex: "vehicle",
      width: "300px",
      render: (_, record) => (
        <p
          style={{ cursor: "pointer" }}
          onClick={() => {
            navigate("/package/destinationdelivery/create", {
              state: { type: "EDIT", destinationDeliveryData: record },
            });
          }}
        >
          {record.vehicle}
        </p>
      ),
    },
    {
      title: t("Destination_Delivery_Table.Table_Depart_At"),
      width: "200px",
      dataIndex: "depart_at",
      render: (_, record) => (record.depart_at != null ? dayjs(record.depart_at).format("YYYY-MM-DD hh:mm:ss A") : "-"),
    },
    {
      title: t("Destination_Delivery_Table.Table_Total_Received"),
      width: "250px",
      dataIndex: "total_received",
    },
    {
      title: t("Destination_Delivery_Table.Table_Total_Count"),
      dataIndex: "total_count",
      width: "250px",
    },
    {
      title: t("Destination_Delivery_Table.Table_Total_Volume"),
      dataIndex: "total_volume",
      width: "250px",
    },
    {
      title: t("Destination_Delivery_Table.Table_Total_Weight"),
      dataIndex: "total_weight",
      width: "250px",
    },
    {
      title: t("Destination_Delivery_Table.Table_Type"),
      width: "300px",
      dataIndex: "delivery_type",
      render: (_, record) => record.delivery_type || "",
    },
    {
      title: t("Destination_Delivery_Table.Table_Status"),
      dataIndex: "delivery_status",
      width: "300px",
      render: (_, record) => {
        const label = formatDeliveryStatusLabel(record.delivery_status, i18n.language, tOps);
        return label != null && label !== "" ? label : "-";
      },
    },
    {
      title: t("Craete_At", { ns: "common" }),
      dataIndex: "created_at",
      width: "200px",
      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD hh:mm:ss A") : "-"),
    },
    {
      title: t("Update_At", { ns: "common" }),
      dataIndex: "updated_at",
      width: "200px",
      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD hh:mm:ss A") : "-"),
    },
    {
      title: t("Edit", { ns: "common" }),
      key: "edit",
      width: "150px",
      render: (_, row) => (
        <Button
          type="link"
          disabled={!hasPermission(permissions.DestinationDelivery.CHANGE)}
          onClick={() => {
            navigate("/package/destinationdelivery/create", {
              state: { type: "EDIT", destinationDeliveryData: row },
            });
          }}
        >
          <EditOutlined />
        </Button>
      ),
    },
    {
      title: t("Delete", { ns: "common" }),
      key: "delete",
      width: "150px",
      render: (_, record) => (
        <Button
          type="link"
          disabled={!hasPermission(permissions.DestinationDelivery.DELETE)}
          onClick={() => {
            setIsOpenDelete(true);
            setPrepareDeleteData(record);
          }}
        >
          <DeleteOutlined />
        </Button>
      ),
    },
  ];
  const items = [
    { title: <span>{t("Page_Body_Tab_Menu.Home", { ns: "menus" })}</span> },
    { title: <span>{t("Page_Body_Tab_Menu.Menu", { ns: "menus" })}</span> },
    { title: t("Menu_Package.Title_Package", { ns: "menus" }) },
    {
      title: (
        <a href="/package/destinationdelivery" style={{ fontWeight: 600, color: "black" }}>
          {t("Menu_Package.Destination_Delivery", { ns: "menus" })}
        </a>
      ),
    },
  ];

  const handleDateChange = useCallback((dates, dateStrings) => {
    setDateRange(dateStrings);
  }, []);

  const disableFutureDates = useCallback((current) => {
    return current && current.isAfter(dayjs().endOf("day"));
  }, []);
  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row flex={1}>
          <Col span={24}>
            <Breadcrumb items={items} />
            <Row style={{ padding: "15px 0px" }}>
              <Col span={24}>
                <Title
                  level={4}
                  style={{
                    textWrap: "nowrap",
                    display: "flex",
                    gap: "7px",
                  }}
                >
                  <ArrowLeftOutlined style={{ fontSize: "20px" }} />
                  {t("Title_Destination_Delivery")}
                </Title>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row style={{ display: "flex", justifyContent: "left", overflowX: "auto" }}>
          <Col span={12}>
            <Form
              form={form}
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              labelAlign="left"
              layout="horizontal"
              onFinish={onFinish}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  form.submit();
                }
              }}
              initialValues={initialValues}
            >
              <Row flex={1}>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item name="depart_at" label={t("Search.Depart_At")}>
                    <RangePicker
                      allowEmpty
                      style={{ width: "100%", minWidth: 200 }}
                      onChange={handleDateChange}
                      disabledDate={disableFutureDates}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Search", { ns: "common" })} name="search" key="search">
                    <Input placeholder="ID / Vehicle" style={{ minWidth: 200 }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Search.Delivery_Type")} name="delivery_type" key="delivery_type">
                    <Select
                      style={{ width: "100%", minWidth: 200 }}
                      placeholder={"delivery type"}
                      options={selectDeliveryType}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Search.Delivery_Status")} name="delivery_status" key="delivery_status">
                    <Select
                      style={{ width: "100%", minWidth: 200 }}
                      placeholder={"delivery status"}
                      options={selectDeliveryStatus}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" align="middle">
                <Col span={24} style={{ textAlign: "end" }}>
                  <Form.Item>
                    <Button type="primary" htmlType="submit">
                      {t("BTN_Search", { ns: "buttons" })}
                    </Button>
                    <Button type="default" onClick={() => reset()} style={{ marginLeft: "5px" }}>
                      {t("BTN_Reset", { ns: "buttons" })}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <div className="flex justify-between mb-4">
              {hasPermission(permissions.DestinationDelivery.DELETE) ? (
                <Button
                  type="primary"
                  disabled={isEmpty(selectedRowKeys)}
                  onClick={() => {
                    setIsOpenDelete(true);
                    setPrepareDeleteData(selectedRowKeys);
                  }}
                >
                  {t("Button_Destination_Delivery.BTN_Delte_All")}
                </Button>
              ) : (
                <div />
              )}
              <Space direction="inline">
                {hasPermission(permissions.DestinationDelivery.EXPORT) && (
                  <Button disabled={!isOpenButton} type="primary" onClick={handleExport}>
                    {t("BTN_Export_Excel", { ns: "buttons" })}
                  </Button>
                )}
                {hasPermission(permissions.DestinationDelivery.ADD) && (
                  <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={() => {
                      navigate("/package/destinationdelivery/create", {
                        state: { type: "CREATE" },
                      });
                    }}
                  >
                    {t("Button_Destination_Delivery.BTN_Add")}
                  </Button>
                )}
              </Space>
            </div>
          </Col>
        </Row>
        <Row align="middle" justify="center">
          <Col span={24}>
            <Row align="middle" justify="center">
              {/* <Col span={24}>
                {`select: ${countSelect} to ${total}`}&nbsp;
                <Link onClick={onSelectAll} target="_blank">
                  {`select all`}
                </Link>
              </Col> */}
            </Row>
            <Card>
              <Table
                loading={loading}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={destinationDeliveryData}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
                pagination={{
                  defaultPageSize: search.limit,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  current: search.page,
                  pageSize: search.limit,
                  total: total,
                  onChange: (page, limit) => {
                    onSearch({ ...tempSearch, page, limit });
                  },
                  showSizeChanger: true,
                }}
                style={{ cursor: "pointer" }}
              />
            </Card>
          </Col>
        </Row>
        <ModalDelete
          isOpen={isOpenDelete}
          handleCloseModal={() => {
            setIsOpenDelete(false);
          }}
          onClick={() => {
            onDeleteDestinationDelivery(prepareDeleteData);
          }}
          dataDetail={prepareDeleteData}
          t={t}
        />
      </Spin>
    </>
  );
};
